import type { KeyboardEvent as ReactKeyboardEvent } from 'react'
import { type Observable, fromEvent } from 'rxjs'
import {
  share,
  map,
  repeat,
  distinctUntilChanged,
  takeUntil,
} from 'rxjs/operators'
import { deepEqual } from './object-util'
import { match } from './pattern-match-util'

/**
 * Returns a stream of keys as the user presses them.
 *
 * @param eventTarget the target to subscribe to. Should be `window.document`.
 */
export function keyboardStream(eventTarget: EventTarget): Observable<KeyPress> {
  return fromEvent<KeyboardEvent>(eventTarget, 'keydown').pipe(
    map((event): KeyPress => ({
      key: event.key,
      ctrl: event.ctrlKey,
      shift: event.shiftKey,
      alt: event.altKey,
      meta: event.metaKey,
      stopPropagation: () => event.stopPropagation(),
      preventDefault: () => event.preventDefault(),
    })),
    distinctUntilChanged(deepEqual),
    takeUntil(fromEvent(window.document, 'keyup')),
    repeat(),
    share(),
  )
}

/**
 * Key press.
 */
export interface KeyPress {
  /**
   * The textual name of the key.
   */
  key: string
  /**
   * Whether shift was held.
   */
  shift: boolean
  /**
   * Whether Ctrl was held.
   */
  ctrl: boolean
  /**
   * Whether Alt was held.
   */
  alt: boolean
  /**
   * Whether the Meta (Win, Cmd, Super) was held.
   */
  meta: boolean
  /**
   * Prevents default.
   */
  preventDefault(): void
  /**
   * Stops propagation.
   */
  stopPropagation(): void
}

/**
 * Minimal representation of a keyboard event.
 */
export interface KeyboardEventLike {
  code: string
  altKey: boolean
  ctrlKey: boolean
  metaKey: boolean
  shiftKey: boolean
}

/**
 * Checks if an event matches a hotkey.
 *
 * Check {@link https://keycode.info} for codes.
 * Use `Ctrl`, `Alt`, `Meta` and `Shift` for modifiers.
 *
 * @param hotkey
 * @param eventLike
 */
export function matchesHotkey(hotkey: string[], eventLike: KeyboardEventLike) {
  return hotkey.every((code) =>
    match(code, {
      Shift: () => eventLike.shiftKey,
      Ctrl: () => eventLike.ctrlKey,
      Alt: () => eventLike.altKey,
      Meta: () => eventLike.metaKey,
      [match.DEFAULT]: () => code === eventLike.code,
    }),
  )
}

/**
 * Gets the label to display for a hotkey as a textual representation.
 *
 * @param hotkey
 */
export function getHotkeyLabel(hotkey: string[]) {
  return hotkey.map(getKeycodeLabel).join('+')
}

/**
 * Gets the label to display for a key code as a textual representation.
 *
 * @param code
 */
export function getKeycodeLabel(code: string) {
  return code.replace(/Key/g, '').replace(/Digit/g, '')
}

/**
 * `onKeyDown` handler that gives a non-button element native button keyboard
 * semantics by dispatching a real click, so the element's `onClick` runs unchanged.
 */
export function clickOnActivationKey(e: ReactKeyboardEvent<HTMLElement>) {
  // Keys bubbling up from nested controls belong to those controls.
  if (e.target !== e.currentTarget) {
    return
  }
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault()
    e.currentTarget.click()
  }
}
